import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Compact, scrollable history list. Pass:
//   items: array of {id, primary, secondary, body}
//   title: string for the modal
// Renders a tight stack of clickable rows. Clicking opens a modal showing `body`.
//
// "Without taking up too much realestate" — capped at ~4 visible rows with
// internal scroll, and each row is one line.
export default function HistoryList({ items, title, emptyHint, badge }) {
  const [openItem, setOpenItem] = useState(null);

  if (!items || items.length === 0) {
    return emptyHint ? (
      <p className="text-zinc-500 text-xs italic mt-2">{emptyHint}</p>
    ) : null;
  }

  return (
    <>
      <div className="mt-4 border-t border-white/5 pt-3">
        <p className="text-zinc-400 text-xs uppercase tracking-wide mb-2">
          {title} <span className="text-zinc-600 normal-case tracking-normal">({items.length})</span>
        </p>
        <div className="max-h-44 overflow-y-auto pr-1 space-y-1">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => setOpenItem(item)}
              className="w-full text-left bg-zinc-950 border border-white/5 hover:border-burns-gold rounded px-3 py-2 transition-colors flex items-center justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-zinc-200 text-sm truncate">{item.primary}</p>
                {item.secondary && (
                  <p className="text-zinc-500 text-xs truncate">{item.secondary}</p>
                )}
              </div>
              {badge && item.badge && (
                <span className={"text-[10px] uppercase tracking-wide px-2 py-0.5 rounded " + (item.badge.className || "bg-zinc-800 text-zinc-300")}>
                  {item.badge.label}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!openItem} onOpenChange={(o) => { if (!o) setOpenItem(null); }}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-burns-gold text-lg">
              {openItem?.primary}
            </DialogTitle>
            {openItem?.secondary && (
              <p className="text-zinc-500 text-xs">{openItem.secondary}</p>
            )}
          </DialogHeader>
          <div className="text-zinc-200 text-sm whitespace-pre-wrap mt-2">
            {openItem?.body}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
